import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useClients } from 'src/clients/hooks/useClients';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import useFinancialEntityLayout from 'src/UI/dynamicUI/hooks/useFinancialEntityLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  useFamilyMembers,
  type FamilyMember,
} from 'src/non-financial-data/v2/family/useFamilyMembers';
import {
  normaliseOwnersForPayload,
  productSupportsOwners,
  type OwnerRowValue,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import {
  financialItemPath,
  RETURN_TO_PARAM,
} from 'src/financial/financial-data/financial-item/financialItemPaths';
import { AssetUpdateChoiceModal, ProviderReferenceModal } from './AssetCreationModals';
import { FamilyHolderModal } from './FamilyHolderModal';
import { useLikelyCopyCheck } from './useLikelyCopyCheck';
import { FamilyOwnerModal, type OwnerSelection } from './FamilyOwnerModal';
import { newItemHolderId } from './newItemHolder';
import { type CreateFlowState } from './forms/common/OwnerSharesSection';

type PendingCreate = { category: FinancialCategory; product: string };

const internalOwnerIdsOf = (owners: OwnerSelection[]) =>
  owners.flatMap(owner => ('client_id' in owner ? [owner.client_id] : []));

type UseCreateFinancialItemArgs = {
  clientId: string;
  onNavigate?: () => void;
  // The entity layout is needed to build a connected asset on submit. Callers
  // that let the user browse products before committing (the picker) pass the
  // current selection so the layout is prefetched rather than fetched only once
  // creation begins.
  prefetchEntityType?: string;
  // `useFamilyMembers` resolves the family from the current client, and the family route has
  // none. Callers already holding the roster pass it in, or the owner step would be skipped
  // there and every member added as an owner unasked.
  members?: FamilyMember[];
  // The item hangs off one client whoever added it, so a caller that is not the client's own
  // Financials says where saving should return to.
  returnTo?: string;
  // Set by a view that spans a household, where `clientId` is only the member the view is
  // anchored on rather than the one the item is for. An item that cannot record owners hangs
  // off whoever it is created against and nobody else, so the adviser is asked which member
  // that is instead of it falling to the anchor unasked.
  chooseHolder?: boolean;
  // A client's own page keeps that client ticked as an owner, since the item is opened through
  // them afterwards. The family view has no such client, so it passes false and the item is
  // created through the first member picked if `clientId` is not among the owners.
  lockClientAsOwner?: boolean;
};

export function useCreateFinancialItem({
  clientId,
  onNavigate,
  prefetchEntityType,
  members: providedMembers,
  returnTo,
  chooseHolder = false,
  lockClientAsOwner = true,
}: UseCreateFinancialItemArgs) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingCreate | null>(null);
  const [ownerModalOpened, setOwnerModalOpened] = useState(false);
  const [holderModalOpened, setHolderModalOpened] = useState(false);
  // Which member the item is being added for. The anchor until the adviser says otherwise, and
  // what every path below creates against: the URL of the form, and the connected-asset upsert.
  const [holderClientId, setHolderClientId] = useState(clientId);
  const [selectedOwners, setSelectedOwners] = useState<OwnerSelection[]>([]);
  const [choiceModalOpened, setChoiceModalOpened] = useState(false);
  const [providerModalOpened, setProviderModalOpened] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'connection' | 'manually'>('connection');
  const [isUpserting, setIsUpserting] = useState(false);

  const { flagEnabledFinancialItemOwners } = useFlags();
  const { members: resolvedMembers, hasOtherMembers: resolvedHasOtherMembers } = useFamilyMembers({
    enabled: providedMembers === undefined && flagEnabledFinancialItemOwners,
  });
  const members = providedMembers ?? resolvedMembers;
  const hasOtherMembers =
    providedMembers === undefined
      ? resolvedHasOtherMembers
      : providedMembers.some(member => member.clientId !== clientId);

  const creatingClientId = (owners: OwnerSelection[], startingClientId = holderClientId) =>
    newItemHolderId(owners, startingClientId, new Set(members.map(member => member.clientId)));
  const internalOwnerIds = internalOwnerIdsOf(selectedOwners);
  const showOwnerStep = flagEnabledFinancialItemOwners && hasOtherMembers;
  const { clients: otherClients } = useClients({}, { enabled: showOwnerStep });

  const { upsertEntity } = useUpsertEntity();
  const { chooseSameCopies, foldCopies, addOwnersToCopy, likelyCopiesModal } = useLikelyCopyCheck({
    members,
  });
  const { layout, isLoading: isLoadingLayout } = useFinancialEntityLayout({
    entityType: pending?.product ?? prefetchEntityType,
  });
  // Scoped to the member the asset is created against: the connect endpoint reaches it through
  // their client, so connecting as the anchor would not find an asset filed under somebody else.
  const { connectAsset } = useConnectAsset({ clientId: holderClientId });

  const navigateAndClose = (path: string, owners: OwnerSelection[] = []) => {
    // The chosen owners travel in the navigation state, not the address: an external owner is
    // named by the adviser, and a name has no business being in a URL that is logged, shared and
    // left in the browser's history.
    const state: CreateFlowState = { owners, holderLocked: lockClientAsOwner };
    navigate(path, owners.length > 0 ? { state } : undefined);
    onNavigate?.();
  };

  const buildCreateUrl = (
    product: string,
    extra?: Record<string, string>,
    owners: OwnerSelection[] = selectedOwners,
    holder: string = creatingClientId(owners),
  ) => {
    const params = new URLSearchParams(extra);
    if (returnTo) {
      params.set(RETURN_TO_PARAM, returnTo);
    }
    if (owners.length > 0) {
      // Only how many owners there are reaches the address, which the joint default reads back
      // on arrival (useJointOwnerDefault); it names nobody. Two owners make an item joint
      // whether or not either sits outside the household, matching `resolve_is_joint`.
      params.set('joint', owners.length >= 2 ? 'true' : 'false');
    }
    const query = params.toString();
    return `/client/${holder}/financial-items/create/${encodeURIComponent(product)}${query ? `?${query}` : ''}`;
  };

  const proceedAfterOwners = (
    category: FinancialCategory,
    product: string,
    owners: OwnerSelection[],
  ) => {
    if (category === 'ASSET') {
      setChoiceModalOpened(true);
    } else {
      navigateAndClose(buildCreateUrl(product, undefined, owners), owners);
    }
  };

  // `initialOwners` pre-ticks the owner picker for callers that already know the likely answer —
  // the family financial panel, where an item added from the family view usually belongs to the
  // whole family. The adviser still confirms, so in a family of three or more an item is not
  // silently owned by everyone. Two or more internal owners make it joint, via buildCreateUrl.
  const beginCreate = (
    category: FinancialCategory,
    product: string,
    initialOwners?: OwnerSelection[],
  ) => {
    setPending({ category, product });
    // Crypto, collectibles and pensions have no owner table, and their forms never render the
    // owners section, so an owners payload would be dropped on save. Carrying owners into one
    // would promise an ownership split the item cannot keep.
    const owners = initialOwners && productSupportsOwners(product) ? initialOwners : [];
    setSelectedOwners(owners);
    setHolderClientId(clientId);
    if (productSupportsOwners(product)) {
      if (showOwnerStep) {
        setOwnerModalOpened(true);
      } else {
        proceedAfterOwners(category, product, owners);
      }
      return;
    }
    // Nothing about this product can record an owner, so the member it is created against is
    // the whole of the answer to who it belongs to. Where that is a choice, it is the adviser's.
    if (chooseHolder && hasOtherMembers) {
      setHolderModalOpened(true);
    } else {
      proceedAfterOwners(category, product, owners);
    }
  };

  const handleHolderContinue = (chosenClientId: string) => {
    setHolderModalOpened(false);
    setHolderClientId(chosenClientId);
    if (!pending) return;
    // Passed rather than read back off the state this set, which this render will not see.
    if (pending.category === 'ASSET') {
      setChoiceModalOpened(true);
    } else {
      navigateAndClose(buildCreateUrl(pending.product, undefined, [], chosenClientId));
    }
  };

  const handleOwnersContinue = (owners: OwnerSelection[]) => {
    setOwnerModalOpened(false);
    setSelectedOwners(owners);
    setHolderClientId(creatingClientId(owners, clientId));
    if (pending) {
      proceedAfterOwners(pending.category, pending.product, owners);
    }
  };

  const handleContinue = () => {
    setChoiceModalOpened(false);
    if (!pending) return;
    if (selectedMethod === 'manually') {
      navigateAndClose(buildCreateUrl(pending.product), selectedOwners);
    } else {
      setProviderModalOpened(true);
    }
  };

  const connectAndNotify = async (assetId: string, saved: string) => {
    try {
      await connectAsset(assetId);

      notifications.show({
        title: 'Success',
        message: 'Asset connection created and populated successfully',
        color: 'green',
      });
    } catch (connectError) {
      if (!ApiError.isApiError(ApiErrorCode.NO_UNPROCESSED_VALUATION_UPDATE, connectError)) {
        Logger.warn('Failed to connect asset to feed after creation', {
          connectError,
          assetId,
        });
      }

      notifications.show({
        title: 'Partially successful',
        message: `${saved} successfully, but failed to connect to feed data. You can manually connect it later.`,
        color: 'yellow',
      });
    }
  };

  const handleProviderSubmit = async (identifiers: {
    providerReference?: string;
    policyNumber?: string;
  }) => {
    setProviderModalOpened(false);
    const product = pending?.product;
    const { providerReference, policyNumber } = identifiers;

    if (pending?.category === 'ASSET' && product && holderClientId && layout) {
      try {
        setIsUpserting(true);

        const metadata = layout.entitiesMetadata?.[product];
        if (!metadata) {
          throw new Error(`No metadata found for entity type: ${product}`);
        }

        const includeOwners = flagEnabledFinancialItemOwners && selectedOwners.length > 0;
        const entityValues = {
          ...(providerReference && { provider_reference_number: providerReference }),
          ...(policyNumber && { policy_number: policyNumber }),
          ...(includeOwners
            ? { owners: normaliseOwnersForPayload(selectedOwners as OwnerRowValue[]) }
            : {}),
        };

        // A co-owner's copy with this reference already holds the asset's details, so the new
        // owners are added to it rather than to a new asset holding only the reference.
        const sameCopies = await chooseSameCopies(
          { product, clientId: holderClientId, fields: entityValues },
          'use-theirs',
        );
        if (sameCopies === null) return;
        const [theirs, ...otherCopies] = sameCopies;

        let assetId: string | null;
        if (theirs) {
          await addOwnersToCopy(theirs, internalOwnerIds);
          assetId = theirs.item_id;
        } else {
          const upsertInput: UpsertEntityInput = {
            entityFieldsMetadata: metadata,
            entityType: product,
            values: entityValues,
          };
          assetId = await upsertEntity(holderClientId, upsertInput, true);
        }
        if (!assetId) {
          throw new Error('Failed to create asset connection');
        }

        if (theirs?.is_connected) {
          notifications.show({
            title: 'Success',
            message: 'Owners added to the connected asset',
            color: 'green',
          });
        } else {
          await connectAndNotify(assetId, theirs ? 'Owners added' : 'Asset created');
        }
        await foldCopies(holderClientId, assetId, otherCopies);

        // Unlike a finished save, which hands back to the Financials, the asset opens: it holds
        // only the reference typed until the feed fills it in, and the rest is the adviser's to add.
        navigateAndClose(financialItemPath(holderClientId, product, assetId, returnTo));
      } catch (error) {
        notifyAndLogError(
          'Error creating asset connection',
          'Something went wrong trying to create the asset connection. Please try again and contact support if the issue persists.',
          error,
          { entityType: product, providerReference, policyNumber },
        );
      } finally {
        setIsUpserting(false);
      }
    } else {
      const extra: Record<string, string> = {};
      if (providerReference) extra.providerReference = providerReference;
      if (policyNumber) extra.policyNumber = policyNumber;
      navigateAndClose(buildCreateUrl(product ?? '', extra), selectedOwners);
    }
  };

  const initialClientIds = internalOwnerIds.length > 0 ? internalOwnerIds : undefined;

  const modals = (
    <>
      <FamilyHolderModal
        opened={holderModalOpened}
        onClose={() => setHolderModalOpened(false)}
        members={members}
        currentClientId={clientId}
        category={pending?.category}
        onContinue={handleHolderContinue}
      />
      <FamilyOwnerModal
        opened={ownerModalOpened}
        onClose={() => setOwnerModalOpened(false)}
        members={members}
        otherClients={otherClients}
        currentClientId={clientId}
        category={pending?.category}
        initialClientIds={initialClientIds}
        lockedClientId={lockClientAsOwner ? clientId : undefined}
        onContinue={handleOwnersContinue}
      />
      <AssetUpdateChoiceModal
        opened={choiceModalOpened}
        onClose={() => setChoiceModalOpened(false)}
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        onContinue={handleContinue}
      />
      <ProviderReferenceModal
        opened={providerModalOpened}
        onClose={() => setProviderModalOpened(false)}
        onSubmit={handleProviderSubmit}
        isLoading={isUpserting || isLoadingLayout}
        clientId={holderClientId}
        product={pending?.product}
      />
      {likelyCopiesModal}
    </>
  );

  return { beginCreate, modals };
}
